import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import { geocodeLocation } from './geoUtils';
import {
  formatWantedLocationLabel,
  GEAR_WANTED_RADIUS_MILES,
  isWantedPostStale,
  isWantedTripDatePassed,
  pickRandomItems,
} from './gearWantedUtils';
import { findOrStartWantedConversation } from './messagingUtils';
import { formatRentalDateRange } from './dateUtils';
import { formatGearLocationLabel } from './gearReviewSummaries';
import { GEAR_IMAGE_PLACEHOLDER } from './rentalPayments';
import { ProfileChip } from './ProfileLink';
import { renderStarRating } from './reviewUtils';
import { useToast } from './Toast';

export function GearWantedPostForm({ user, categories = [], onPosted, onLogin }) {
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState('');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!user) {
      onLogin?.();
      showToast('Please sign in to post a gear wanted ad.', 'info');
      return;
    }

    const trimmedItem = item.trim();
    const trimmedLocation = location.trim();
    if (!trimmedItem || !trimmedLocation) {
      showToast('Add what you are looking for and a location.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const coords = await geocodeLocation(trimmedLocation);
      if (!coords) {
        showToast('Could not find that location. Try a city, state, or zip code.', 'error');
        return;
      }

      const createdAt = new Date().toISOString();
      await addDoc(collection(db, 'gearWanted'), {
        requesterId: user.uid,
        item: trimmedItem,
        category: category || '',
        locationLabel: trimmedLocation,
        coordinates: {
          latitude: coords.lat,
          longitude: coords.lon,
        },
        radiusMiles: GEAR_WANTED_RADIUS_MILES,
        startDate: startDate || '',
        endDate: endDate || '',
        notes: notes.trim(),
        status: 'open',
        createdAt,
      });

      setItem('');
      setLocation('');
      setCategory('');
      setStartDate('');
      setEndDate('');
      setNotes('');
      setOpen(false);
      showToast('Your request is live. Manage it anytime in Your Hangar.', 'success');
      onPosted?.();
    } catch (error) {
      console.error('Error posting gear wanted:', error);
      showToast('Could not post your request. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="gear-wanted" className="gear-wanted-post" aria-label="Post gear wanted">
      <div className="gear-wanted-post-inner">
        <div className="gear-wanted-post-copy">
          <h2 className="home-section-title">Looking for something?</h2>
          <p className="home-section-lead">
            Post what you need and where — owners within {GEAR_WANTED_RADIUS_MILES} miles can message you if they can help.
            Add trip dates if you are planning ahead; your ad stays visible through the end of that window.
          </p>
        </div>
        {!open ? (
          <button type="button" className="gear-wanted-post-toggle" onClick={() => setOpen(true)}>
            Post gear wanted
          </button>
        ) : (
          <form className="gear-wanted-post-form" onSubmit={handleSubmit}>
            <GearWantedFormFields
              item={item}
              setItem={setItem}
              location={location}
              setLocation={setLocation}
              category={category}
              setCategory={setCategory}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              notes={notes}
              setNotes={setNotes}
              categories={categories}
            />
            <div className="gear-wanted-post-actions">
              <button type="submit" disabled={submitting}>
                {submitting ? 'Posting…' : 'Post request'}
              </button>
              <button type="button" className="gear-wanted-post-cancel" onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function GearWantedFormFields({
  item,
  setItem,
  location,
  setLocation,
  category,
  setCategory,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  notes,
  setNotes,
  categories = [],
}) {
  return (
    <>
      <input
        type="text"
        placeholder="What do you need? (e.g. kayak, 4-person tent)"
        value={item}
        onChange={(e) => setItem(e.target.value)}
        maxLength={120}
        required
      />
      <input
        type="text"
        placeholder="Location (city, state, or zip)"
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        required
      />
      <select value={category} onChange={(e) => setCategory(e.target.value)}>
        <option value="">Category (optional)</option>
        {categories.map((cat) => (
          <option key={cat.id} value={cat.id}>{cat.name}</option>
        ))}
      </select>
      <div className="gear-wanted-post-dates">
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>
      <textarea
        placeholder="Optional details (size, trip dates, pickup notes)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        maxLength={280}
      />
    </>
  );
}

export function MyGearWantedSection({ user, categories = [] }) {
  const { showToast } = useToast();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [item, setItem] = useState('');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadPosts = useCallback(async () => {
    if (!user?.uid) {
      setPosts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const snapshot = await getDocs(
        query(collection(db, 'gearWanted'), where('requesterId', '==', user.uid))
      );
      const nextPosts = snapshot.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .filter((post) => post.status === 'open')
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setPosts(nextPosts);
    } catch (error) {
      console.error('Error loading your gear wanted posts:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const resetForm = () => {
    setEditingId(null);
    setItem('');
    setLocation('');
    setCategory('');
    setStartDate('');
    setEndDate('');
    setNotes('');
  };

  const startEditing = (post) => {
    setEditingId(post.id);
    setItem(post.item || '');
    setLocation(post.locationLabel || formatWantedLocationLabel(post));
    setCategory(post.category || '');
    setStartDate(post.startDate || '');
    setEndDate(post.endDate || '');
    setNotes(post.notes || '');
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const trimmedItem = item.trim();
    const trimmedLocation = location.trim();
    if (!trimmedItem || !trimmedLocation) {
      showToast('Add what you are looking for and a location.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const coords = await geocodeLocation(trimmedLocation);
      if (!coords) {
        showToast('Could not find that location. Try a city, state, or zip code.', 'error');
        return;
      }

      await updateDoc(doc(db, 'gearWanted', editingId), {
        item: trimmedItem,
        category: category || '',
        locationLabel: trimmedLocation,
        coordinates: {
          latitude: coords.lat,
          longitude: coords.lon,
        },
        startDate: startDate || '',
        endDate: endDate || '',
        notes: notes.trim(),
        updatedAt: new Date().toISOString(),
      });

      showToast('Your gear wanted ad was updated.', 'success');
      resetForm();
      await loadPosts();
    } catch (error) {
      console.error('Error updating gear wanted:', error);
      showToast('Could not update your request. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRenew = async (postId) => {
    try {
      const renewedAt = new Date().toISOString();
      await updateDoc(doc(db, 'gearWanted', postId), {
        lastRenewedAt: renewedAt,
        createdAt: renewedAt,
        updatedAt: renewedAt,
      });
      showToast('Your request was renewed and bumped back into recent listings.', 'success');
      await loadPosts();
    } catch (error) {
      console.error('Error renewing gear wanted:', error);
      showToast('Could not renew your request. Please try again.', 'error');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Remove this gear wanted ad?')) return;

    try {
      await deleteDoc(doc(db, 'gearWanted', postId));
      if (editingId === postId) resetForm();
      showToast('Gear wanted ad removed.', 'success');
      await loadPosts();
    } catch (error) {
      console.error('Error deleting gear wanted:', error);
      showToast('Could not remove your request. Please try again.', 'error');
    }
  };

  if (!user) return null;

  return (
    <section className="hangar-wanted-section" aria-label="Your gear wanted ads">
      <h3>Gear you&apos;re looking for</h3>
      <p className="hangar-wanted-lead">
        Manage your open requests. If you add trip dates, your ad stays visible through the end of that window.
        Open-ended requests stay live until you remove them.
      </p>

      {loading ? (
        <p className="hangar-wanted-lead">Loading your requests…</p>
      ) : posts.length === 0 ? (
        <p className="hangar-wanted-empty">No open gear wanted ads yet.</p>
      ) : (
        <div className="gear-wanted-list hangar-wanted-list">
          {posts.map((post) => {
            const categoryName =
              categories.find((cat) => cat.id === post.category)?.name || post.category || 'Any category';
            const datesPassed = isWantedTripDatePassed(post);
            const stale = isWantedPostStale(post);
            const isEditing = editingId === post.id;

            return (
              <article key={post.id} className={`gear-wanted-card hangar-wanted-card${datesPassed ? ' is-expired' : ''}`}>
                {isEditing ? (
                  <form className="gear-wanted-post-form hangar-wanted-edit-form" onSubmit={handleSave}>
                    <GearWantedFormFields
                      item={item}
                      setItem={setItem}
                      location={location}
                      setLocation={setLocation}
                      category={category}
                      setCategory={setCategory}
                      startDate={startDate}
                      setStartDate={setStartDate}
                      endDate={endDate}
                      setEndDate={setEndDate}
                      notes={notes}
                      setNotes={setNotes}
                      categories={categories}
                    />
                    <div className="gear-wanted-post-actions">
                      <button type="submit" disabled={submitting}>
                        {submitting ? 'Saving…' : 'Save changes'}
                      </button>
                      <button type="button" className="gear-wanted-post-cancel" onClick={resetForm}>
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <span className="gear-wanted-card-badge">{datesPassed ? 'Dates passed' : 'Wanted'}</span>
                    <h4>{post.item}</h4>
                    <p className="gear-wanted-card-meta">
                      {categoryName} · {formatWantedLocationLabel(post)}
                    </p>
                    <p className="gear-wanted-card-radius">
                      Within {post.radiusMiles || GEAR_WANTED_RADIUS_MILES} miles
                    </p>
                    {post.notes && <p className="gear-wanted-card-notes">{post.notes}</p>}
                    {datesPassed && (
                      <p className="hangar-wanted-status-note">
                        Hidden from homepage and search until you update your trip dates.
                      </p>
                    )}
                    {stale && !datesPassed && (
                      <p className="hangar-wanted-status-note">
                        Still looking? Renew to bump this request back into recent listings.
                      </p>
                    )}
                    <div className="hangar-wanted-card-actions">
                      <button type="button" onClick={() => startEditing(post)}>
                        {datesPassed ? 'Update dates' : 'Edit'}
                      </button>
                      {stale && !datesPassed && (
                        <button type="button" onClick={() => handleRenew(post.id)}>
                          Renew
                        </button>
                      )}
                      <button type="button" className="hangar-wanted-delete" onClick={() => handleDelete(post.id)}>
                        Remove
                      </button>
                    </div>
                  </>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function GearWantedCard({ post, categories = [], user, onLogin }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const categoryName =
    categories.find((cat) => cat.id === post.category)?.name || post.category || 'Any category';
  const dateLabel =
    post.startDate && post.endDate
      ? formatRentalDateRange(post.startDate, post.endDate)
      : post.startDate || post.endDate
        ? formatRentalDateRange(post.startDate || post.endDate, post.endDate || post.startDate)
        : null;

  const handleRespond = async () => {
    if (!user) {
      onLogin?.();
      showToast('Please sign in to message this member.', 'info');
      return;
    }
    if (user.uid === post.requesterId) return;

    setLoading(true);
    try {
      const conversationId = await findOrStartWantedConversation({
        currentUserId: user.uid,
        recipientId: post.requesterId,
        wantedId: post.id,
        item: post.item,
        locationLabel: formatWantedLocationLabel(post),
      });
      navigate('/messaging', {
        state: {
          openConversationId: conversationId,
          pendingConversation: {
            id: conversationId,
            conversationType: 'gear-wanted',
            gearName: `Gear wanted: ${post.item}`,
            otherUserId: post.requesterId,
          },
        },
      });
    } catch (error) {
      showToast(error.message || 'Could not start conversation.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <article className="gear-wanted-card">
      <span className="gear-wanted-card-badge">Wanted</span>
      <h3>{post.item}</h3>
      <p className="gear-wanted-card-meta">
        {categoryName} · {formatWantedLocationLabel(post)}
      </p>
      <p className="gear-wanted-card-radius">Within {post.radiusMiles || GEAR_WANTED_RADIUS_MILES} miles</p>
      {dateLabel && <p className="gear-wanted-card-dates">Dates: {dateLabel}</p>}
      {post.notes && <p className="gear-wanted-card-notes">{post.notes}</p>}
      {user?.uid !== post.requesterId && (
        <button type="button" className="gear-wanted-card-cta" onClick={handleRespond} disabled={loading}>
          {loading ? 'Opening…' : 'I can help'}
        </button>
      )}
    </article>
  );
}

function FeaturedGearCard({ gear, categories }) {
  const categoryName =
    categories.find((cat) => cat.id === gear.category)?.name || gear.category || 'Gear';
  const locationLabel = formatGearLocationLabel(gear.location);
  const reviewSummary = gear.reviewSummary;

  return (
    <Link to={`/gear/${gear.id}`} className="home-featured-card">
      <img
        src={gear.mainPicture || GEAR_IMAGE_PLACEHOLDER}
        alt={gear.name}
        className="home-featured-card-image"
      />
      <div className="home-featured-card-body">
        <h3>{gear.name}</h3>
        {reviewSummary?.count > 0 && (
          <p className="home-featured-card-rating">
            {renderStarRating(reviewSummary.rating)}
            <span style={{ marginLeft: '6px', color: '#6b7280' }}>
              ({reviewSummary.count})
            </span>
          </p>
        )}
        <p className="home-featured-card-meta">
          {categoryName} · {locationLabel}
        </p>
        {gear.ownerId && (
          <p className="home-featured-card-host">
            <ProfileChip userId={gear.ownerId} label="Hosted by" />
          </p>
        )}
        <p className="home-featured-card-price">
          ${gear.pricePerDay || '—'}<span>/day</span>
        </p>
      </div>
    </Link>
  );
}

export function HomeDiscoverySection({
  gear = [],
  wantedPosts = [],
  categories = [],
  user,
  loadingGear = false,
  loadingWanted = false,
  onLogin,
}) {
  const hasWanted = wantedPosts.length > 0;
  const featuredCount = hasWanted ? 2 : 4;
  const displayedGear = useMemo(
    () => pickRandomItems(gear, featuredCount),
    [gear, featuredCount]
  );
  const displayedWanted = wantedPosts.slice(0, 2);
  const loading = loadingGear || loadingWanted;

  return (
    <section className="home-discovery" aria-label="Gear and requests">
      {loading ? (
        <p className="home-section-lead">Loading community listings…</p>
      ) : (
        <>
          {hasWanted ? (
            <div className="home-discovery-split">
              <div className="home-discovery-column">
                <h2 className="home-section-title">Members looking for gear</h2>
                <p className="home-section-lead">Recent requests from renters in the community.</p>
                <div className="gear-wanted-list">
                  {displayedWanted.map((post) => (
                    <GearWantedCard
                      key={post.id}
                      post={post}
                      categories={categories}
                      user={user}
                      onLogin={onLogin}
                    />
                  ))}
                </div>
              </div>
              <div className="home-discovery-column">
                <h2 className="home-section-title">Adventure-ready gear</h2>
                <p className="home-section-lead">Listings from owners ready to share.</p>
                {displayedGear.length > 0 ? (
                  <div className="home-featured-list home-featured-list-compact">
                    {displayedGear.map((item) => (
                      <FeaturedGearCard key={item.id} gear={item} categories={categories} />
                    ))}
                  </div>
                ) : (
                  <p className="home-section-lead">No gear listings yet in this view.</p>
                )}
              </div>
            </div>
          ) : (
            <>
              <h2 className="home-section-title">Adventure-ready gear</h2>
              {displayedGear.length > 0 ? (
                <>
                  <p className="home-section-lead">
                    Real listings from owners who are ready to share their gear — and their favorite places to use it.
                  </p>
                  <div className="home-featured-list">
                    {displayedGear.map((item) => (
                      <FeaturedGearCard key={item.id} gear={item} categories={categories} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="home-featured-empty">
                  <p>New listings are added as owners join. Check back soon — or be among the first to list your gear.</p>
                  <Link to={user ? '/your-hangar' : '/create-profile'} className="home-featured-empty-link">
                    List your gear
                  </Link>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}

export function GearWantedResultsSection({ posts = [], categories = [], user, onLogin }) {
  if (!posts.length) return null;

  return (
    <section className="gear-wanted-results" aria-label="Gear wanted near this search">
      <h3 className="home-search-results-title">Members looking nearby</h3>
      <p className="home-section-lead">
        These requests are within {GEAR_WANTED_RADIUS_MILES} miles of your search area.
      </p>
      <div className="gear-wanted-list">
        {posts.map((post) => (
          <GearWantedCard
            key={post.id}
            post={post}
            categories={categories}
            user={user}
            onLogin={onLogin}
          />
        ))}
      </div>
    </section>
  );
}